<script>
import { SimpleBar } from "simplebar-vue3";
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import api from "@/axios";
import { getBaseUrl } from "@/helpers/baseUrl";

export default {
  components: { Layout, PageHeader, SimpleBar },

  page: {
    title: "WhatsApp",
    meta: [{ name: "description", content: "Espelho WhatsApp" }],
  },

  data() {
    return {
      title: "WhatsApp",
      items: [{ text: "WhatsApp", active: true }],

      sessionState: "loading", // loading | not_provisioned | pending | connected

      qrCode: null,
      qrTimer: null,
      pairPhone: "",
      pairCode: null,
      showPairInput: false,

      chats: [],
      chatListTimer: null,
      searchQuery: "",

      activeChatId: null,
      activeChatName: "",
      messages: [],
      messageTimer: null,

      newMessage: "",
      sending: false,
      historicoPedido: {},
      webhookUrl: "",
      showWebhookModal: false,
      salvandoWebhook: false,
    };
  },

  mounted() {
    this.checkStatus();
    this.startChatListPolling();
  },

  beforeUnmount() {
    this.stopChatListPolling();
    this.stopMessagePolling();
    this.stopQrPolling();
  },

  watch: {
    activeChatId(val) {
      this.stopMessagePolling();
      this.messages = [];
      if (val) {
        this.fetchMessages();
        this.startMessagePolling();
      }
    },
    sessionState(val) {
      if (val === "pending") {
        this.fetchQrCode();
        this.startQrPolling();
      } else {
        this.stopQrPolling();
      }
      if (val === "connected") {
        this.fetchChats();
      }
    },
  },

  computed: {
    filteredChats() {
      if (!this.searchQuery.trim()) return this.chats;
      const q = this.searchQuery.toLowerCase();
      return this.chats.filter((c) => {
        const name = this.chatDisplayName(c).toLowerCase();
        const id = (c.id || "").toLowerCase();
        return name.includes(q) || id.includes(q);
      });
    },
  },

  methods: {
    checkStatus() {
      this.sessionState = "loading";
      api
        .get(`${getBaseUrl()}api/wsapi/status`)
        .then((res) => {
          const d = res.data;
          if (d.error && d.error.includes("não configurada")) {
            this.sessionState = "not_provisioned";
          } else if (d.isConnected && d.isLoggedIn) {
            this.sessionState = "connected";
          } else {
            this.sessionState = "pending";
          }
        })
        .catch(() => {
          this.sessionState = "not_provisioned";
        });
    },

    provisionar() {
      api
        .get(`${getBaseUrl()}api/wsapi/provisionar`)
        .then(() => {
          this.sessionState = "pending";
        })
        .catch(() => {
          alert("Erro ao criar instância. Tente novamente.");
        });
    },

    fetchQrCode() {
      api
        .get(`${getBaseUrl()}api/wsapi/qr`)
        .then((res) => {
          this.qrCode = res.data.qr || null;
        })
        .catch(() => {
          this.qrCode = null;
        });
    },

    startQrPolling() {
      this.qrTimer = setInterval(() => {
        if (this.sessionState !== "pending") {
          this.stopQrPolling();
          return;
        }
        this.fetchQrCode();
        this.checkStatusSilent();
      }, 20000);
    },

    stopQrPolling() {
      clearInterval(this.qrTimer);
      this.qrTimer = null;
    },

    checkStatusSilent() {
      api.get(`${getBaseUrl()}api/wsapi/status`).then((res) => {
        if (res.data.isConnected && res.data.isLoggedIn) {
          this.sessionState = "connected";
        }
      });
    },

    fetchPairCode() {
      if (!this.pairPhone) return;
      api
        .get(`${getBaseUrl()}api/wsapi/paircode/${this.pairPhone}`)
        .then((res) => {
          this.pairCode = res.data.code || null;
        })
        .catch(() => {
          this.pairCode = null;
        });
    },

    startChatListPolling() {
      this.chatListTimer = setInterval(() => {
        if (this.sessionState === "connected") {
          this.fetchChats();
        }
      }, 5000);
    },

    stopChatListPolling() {
      clearInterval(this.chatListTimer);
      this.chatListTimer = null;
    },

    fetchChats() {
      api
        .get(`${getBaseUrl()}api/wsapi/chats`)
        .then((res) => {
          this.chats = Array.isArray(res.data) ? res.data : [];
        })
        .catch(() => {});
    },

    selectChat(chat) {
      this.activeChatId = chat.id;
      this.activeChatName =
        chat.pushName || chat.businessName || chat.fullName || chat.id;
    },

    startMessagePolling() {
      this.messageTimer = setInterval(() => {
        if (this.activeChatId) {
          this.fetchMessages();
        }
      }, 2000);
    },

    stopMessagePolling() {
      clearInterval(this.messageTimer);
      this.messageTimer = null;
    },

    fetchMessages() {
      if (!this.activeChatId) return;
      const encoded = encodeURIComponent(this.activeChatId);
      api
        .get(`${getBaseUrl()}api/wsapi/messages/${encoded}`)
        .then((res) => {
          this.messages = Array.isArray(res.data) ? res.data : [];
          if (
            this.messages.length === 0 &&
            this.activeChatId &&
            !this.historicoPedido[this.activeChatId]
          ) {
            this.requestHistorico();
          }
          this.$nextTick(() => this.scrollToBottom());
        })
        .catch(() => {});
    },

    requestHistorico() {
      this.historicoPedido[this.activeChatId] = true;
      const encoded = encodeURIComponent(this.activeChatId);
      api.get(`${getBaseUrl()}api/wsapi/historico/${encoded}`).catch(() => {});
    },

    sendMessage() {
      if (!this.newMessage.trim() || !this.activeChatId || this.sending) return;
      this.sending = true;
      api
        .post(`${getBaseUrl()}api/wsapi/messages`, {
          to: this.activeChatId,
          text: this.newMessage.trim(),
        })
        .then(() => {
          this.newMessage = "";
          this.fetchMessages();
        })
        .finally(() => {
          this.sending = false;
        });
    },

    logout() {
      api.post(`${getBaseUrl()}api/wsapi/logout`).then(() => {
        this.sessionState = "pending";
        this.chats = [];
        this.messages = [];
        this.activeChatId = null;
      });
    },

    salvarWebhook() {
      if (!this.webhookUrl.trim()) return;
      this.salvandoWebhook = true;
      api
        .post(`${getBaseUrl()}api/wsapi/configurar-webhook`, {
          webhook_url: this.webhookUrl.trim(),
        })
        .then((res) => {
          alert(
            "Webhook salvo!\n\nConfigure esta URL na instância WSAPI no Coolify:\n" +
            res.data.endpoint
          );
          this.showWebhookModal = false;
        })
        .catch(() => {
          alert("Erro ao salvar webhook.");
        })
        .finally(() => {
          this.salvandoWebhook = false;
        });
    },

    scrollToBottom() {
      const el = this.$refs.msgContainer;
      if (el && el.$el) {
        const scrollEl = el.SimpleBar?.value?.getScrollElement?.();
        if (scrollEl) scrollEl.scrollTop = scrollEl.scrollHeight;
      }
    },

    chatDisplayName(chat) {
      if (chat.pushName) return chat.pushName;
      if (chat.businessName) return chat.businessName;
      if (chat.fullName) return chat.fullName;
      const id = chat.id || "";
      if (id.includes("@g.us")) {
        return "Grupo " + id.replace("@g.us", "").slice(-6);
      }
      return id.replace("@s.whatsapp.net", "").replace("@c.us", "");
    },

    formatTime(ts) {
      if (!ts) return "";
      const d = new Date(ts);
      return d.getHours() + ":" + String(d.getMinutes()).padStart(2, "0");
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />

    <!-- Estado: carregando -->
    <div v-if="sessionState === 'loading'" class="text-center py-5">
      <div class="spinner-border text-success" role="status"></div>
      <p class="mt-2 text-muted">Verificando conexão...</p>
    </div>

    <!-- Estado: não provisionado -->
    <div v-else-if="sessionState === 'not_provisioned'" class="text-center py-5">
      <i class="mdi mdi-whatsapp font-size-48 text-success"></i>
      <h4 class="mt-3">Nenhuma instância configurada</h4>
      <p class="text-muted">Crie uma instância WhatsApp para este cliente.</p>
      <button class="btn btn-success btn-lg mt-2" @click="provisionar">
        <i class="mdi mdi-plus me-1"></i> Criar instância WhatsApp
      </button>
    </div>

    <!-- Estado: pareamento -->
    <div v-else-if="sessionState === 'pending'" class="text-center py-4">
      <h4>Conectar WhatsApp</h4>
      <p class="text-muted">Escaneie o QR Code com seu WhatsApp ou use o código de pareamento.</p>

      <div v-if="!showPairInput">
        <div v-if="qrCode" class="d-inline-block border p-3 rounded bg-white shadow-sm">
          <img :src="'data:image/png;base64,' + qrCode" alt="QR Code" style="width:220px;height:220px;" />
        </div>
        <div v-else class="py-4 text-muted">
          <div class="spinner-border spinner-border-sm me-2"></div> Carregando QR Code...
        </div>
        <p class="text-muted mt-2" style="font-size:12px;">Atualiza automaticamente a cada 20 segundos</p>
      </div>

      <div v-if="showPairInput" class="mt-3" style="max-width:320px;margin:0 auto;">
        <div class="input-group">
          <input
            v-model="pairPhone"
            type="text"
            class="form-control"
            placeholder="Ex: 5527999990000"
          />
          <button class="btn btn-success" @click="fetchPairCode">Gerar código</button>
        </div>
        <div v-if="pairCode" class="mt-3">
          <h3 class="text-success letter-spacing-lg">{{ pairCode }}</h3>
          <p class="text-muted">Digite este código no WhatsApp → Dispositivos conectados → Vincular dispositivo</p>
        </div>
      </div>

      <div class="mt-3">
        <button class="btn btn-outline-secondary btn-sm" @click="showPairInput = !showPairInput">
          {{ showPairInput ? 'Usar QR Code' : 'Usar código de pareamento' }}
        </button>
      </div>
    </div>

    <!-- Estado: conectado — Espelho WhatsApp -->
    <div v-else-if="sessionState === 'connected'" class="d-lg-flex">

      <!-- Sidebar: lista de chats -->
      <div class="chat-leftsidebar me-lg-4">
        <div class="py-3 border-bottom d-flex align-items-center justify-content-between px-2">
          <div class="d-flex align-items-center">
            <i class="mdi mdi-whatsapp text-success font-size-20 me-2"></i>
            <h5 class="mb-0">WhatsApp</h5>
          </div>
          <div class="d-flex gap-2">
            <button class="btn btn-sm btn-outline-secondary" @click="showWebhookModal = true" title="Configurar Webhook">
              <i class="mdi mdi-webhook"></i>
            </button>
            <button class="btn btn-sm btn-outline-danger" @click="logout" title="Desconectar">
              <i class="mdi mdi-logout"></i>
            </button>
          </div>
        </div>

        <div class="search-box chat-search-box py-3">
          <div class="position-relative">
            <input
              type="text"
              v-model="searchQuery"
              class="form-control"
              placeholder="Pesquisar conversa..."
            />
            <i class="bx bx-search-alt search-icon"></i>
          </div>
        </div>

        <SimpleBar style="max-height: 520px" id="chat-list">
          <ul class="list-unstyled chat-list px-2">
            <li
              v-for="chat in filteredChats"
              :key="chat.id"
              @click="selectChat(chat)"
              :class="{ active: activeChatId === chat.id }"
              style="cursor:pointer;"
            >
              <a href="javascript:void(0);">
                <div class="d-flex align-items-center py-2">
                  <div class="avatar-xs me-3">
                    <span class="avatar-title rounded-circle bg-soft bg-success text-success">
                      {{ chatDisplayName(chat).charAt(0).toUpperCase() }}
                    </span>
                  </div>
                  <div class="flex-grow-1 overflow-hidden">
                    <div class="d-flex justify-content-between align-items-center">
                      <h5 class="text-truncate font-size-14 mb-0">{{ chatDisplayName(chat) }}</h5>
                      <span class="text-muted ms-2 flex-shrink-0" style="font-size:11px;">{{ formatTime(chat.lastActivity) }}</span>
                    </div>
                    <p class="text-truncate text-muted mb-0" style="font-size:11px;">
                      <i v-if="chat.isGroup" class="mdi mdi-account-group me-1"></i>
                      <i v-else class="mdi mdi-account me-1"></i>
                      {{ chat.isGroup ? "Grupo" : (chat.id || "").replace("@s.whatsapp.net","").replace("@c.us","") }}
                    </p>
                  </div>
                  <i v-if="chat.isPinned" class="mdi mdi-pin text-muted ms-1" style="font-size:12px;"></i>
                </div>
              </a>
            </li>
            <li v-if="filteredChats.length === 0 && searchQuery" class="text-center text-muted py-4">
              <small>Nenhuma conversa encontrada.</small>
            </li>
            <li v-else-if="chats.length === 0" class="text-center text-muted py-4">
              <div class="spinner-border spinner-border-sm me-1"></div> Carregando chats...
            </li>
          </ul>
        </SimpleBar>
      </div>

      <!-- Painel: conversa ativa -->
      <div class="w-100 user-chat">
        <div class="card">

          <!-- Sem chat selecionado -->
          <div v-if="!activeChatId" class="text-center py-5 text-muted">
            <i class="mdi mdi-message-text-outline font-size-48"></i>
            <p class="mt-2">Selecione uma conversa</p>
          </div>

          <template v-else>
            <div class="p-3 border-bottom d-flex align-items-center justify-content-between">
              <div>
                <h5 class="mb-0">{{ activeChatName }}</h5>
                <small class="text-muted">{{ activeChatId }}</small>
              </div>
            </div>

            <div class="chat-conversation p-3">
              <SimpleBar style="max-height: 420px" ref="msgContainer">
                <ul class="list-unstyled">
                  <li
                    v-for="(msg, idx) in messages"
                    :key="idx"
                    :class="{ right: msg.fromMe }"
                  >
                    <div class="conversation-list">
                      <div class="ctext-wrap">
                        <p class="mb-1">{{ msg.text || msg.body || msg.conversation || '' }}</p>
                        <p class="chat-time mb-0">
                          <i class="bx bx-time-five align-middle me-1"></i>
                          {{ formatTime(msg.timestamp ? msg.timestamp * 1000 : msg.time) }}
                        </p>
                      </div>
                    </div>
                  </li>
                  <li v-if="messages.length === 0" class="text-center text-muted py-3">
                    <div v-if="historicoPedido[activeChatId]">
                      <div class="spinner-border spinner-border-sm me-1"></div>
                      <small>Solicitando histórico... As mensagens aparecem em instantes.</small>
                    </div>
                    <small v-else>Nenhuma mensagem carregada.</small>
                  </li>
                </ul>
              </SimpleBar>
            </div>

            <div class="p-3 chat-input-section">
              <form @submit.prevent="sendMessage" class="row">
                <div class="col">
                  <input
                    type="text"
                    v-model="newMessage"
                    class="form-control chat-input rounded"
                    placeholder="Digite uma mensagem..."
                    :disabled="sending"
                  />
                </div>
                <div class="col-auto">
                  <button
                    type="submit"
                    class="btn btn-success btn-rounded chat-send w-md"
                    :disabled="sending || !newMessage.trim()"
                  >
                    <span class="d-none d-sm-inline-block me-2">Enviar</span>
                    <i class="mdi mdi-send"></i>
                  </button>
                </div>
              </form>
            </div>
          </template>

        </div>
      </div>
    </div>

    <!-- Modal Webhook -->
    <div v-if="showWebhookModal" style="position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.5);z-index:9999;display:flex;align-items:center;justify-content:center;">
      <div class="card p-4" style="width:420px;max-width:95vw;">
        <h5 class="mb-3"><i class="mdi mdi-webhook me-2"></i>Configurar Webhook</h5>
        <p class="text-muted mb-1" style="font-size:13px;">
          1. Execute: <code>ngrok http 8001</code><br>
          2. Cole a URL gerada abaixo (ex: <code>https://abc123.ngrok.io</code>)<br>
          3. Configure <code>WEBHOOK_URL</code> no Coolify com o endpoint retornado
        </p>
        <div class="input-group mt-3">
          <input
            v-model="webhookUrl"
            type="text"
            class="form-control"
            placeholder="https://abc123.ngrok.io"
          />
        </div>
        <div class="d-flex gap-2 mt-3 justify-content-end">
          <button class="btn btn-outline-secondary btn-sm" @click="showWebhookModal = false">Cancelar</button>
          <button
            class="btn btn-success btn-sm"
            @click="salvarWebhook"
            :disabled="salvandoWebhook || !webhookUrl.trim()"
          >
            <span v-if="salvandoWebhook"><div class="spinner-border spinner-border-sm me-1"></div></span>
            Salvar
          </button>
        </div>
      </div>
    </div>

  </Layout>
</template>
